import useDBO from "../../hooks/useDBO";
import Loading from "../../components/Loading";
import Error from "../../components/Error";
import { Link } from "react-router-dom";
import Staff from "../../components/Staff";
import { Clock, MapPin, BookOpen, Users, FileText } from "lucide-react";

/**
 * Plan - renders the list of today's lessons for a given location and date.
 * Shows planned lessons as premium cards and unplanned classes as compact pills.
 *
 * @component
 * @param {Object} props
 * @param {string} props.location
 * @param {string} props.dt
 * @author Forge
 * @date 2026-04-11
 * @version 2.0.0
 */
export default function Plan({ location, dt }) {
  const lessons = useDBO(`/todays-lessons/${location}/${dt}`);

  if (lessons.loading) return <Loading />;
  if (lessons.error) return <Error />;

  lessons.log();

  const planned = lessons.filter(
    (l) => l.lessonOrTest && l.lessonOrTest.type !== "undefined"
  );
  const unplanned = lessons.filter(
    (l) => !l.lessonOrTest || l.lessonOrTest.type === "undefined"
  );

  return (
    <div className="space-y-4">
      {planned.length > 0 && (
        <div className="flex items-center gap-2 mb-1">
          <div className="text-[0.7rem] font-semibold text-slate-400 uppercase tracking-wider">
            {planned.length} planned
          </div>
          <div className="h-px flex-1 bg-slate-200" />
        </div>
      )}

      {planned.map((l, i) => {
        if (l.lessonOrTest.lesson)
          return <DrawLesson key={l._id || i} lesson={l} />;
        if (l.lessonOrTest.test) return <DrawTest key={l._id || i} lesson={l} />;
        return (
          <div
            key={l._id || i}
            className="text-xs text-slate-400 px-4 py-3 bg-slate-50 rounded-[10px] border border-slate-100"
          >
            No information
          </div>
        );
      })}

      {unplanned.length > 0 && (
        <div className="pt-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="text-[0.7rem] font-semibold text-slate-400 uppercase tracking-wider">
              {unplanned.length} unplanned
            </div>
            <div className="h-px flex-1 bg-slate-200" />
          </div>
          <div className="flex flex-wrap gap-2">
            {unplanned.map((lesson, i) => (
              <Link
                key={lesson._id || i}
                to={`/class/${lesson.htClass}`}
                className="text-xs px-3 py-1.5 rounded-[10px] bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900 transition-colors duration-150"
              >
                {lesson.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * DrawTest - renders a test-type lesson as a compact card.
 * @component
 * @param {Object} props
 * @param {Object} props.lesson
 * @author Forge
 * @date 2026-04-11
 * @version 2.0.0
 */
function DrawTest({ lesson }) {
  return (
    <div className="bg-white border border-slate-200 rounded-[10px] p-5 shadow-[0_8px_30px_rgba(0,0,0,0.08)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.12)] hover:border-slate-300 transition-all duration-150">
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-amber-400" />
        <span className="text-sm font-semibold text-slate-700">Test</span>
        <span className="text-sm text-slate-500">— {lesson.name}</span>
      </div>
    </div>
  );
}

/**
 * DrawLesson - renders a single lesson as a premium flat card with
 * class info, teacher details, and material requests.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.lesson
 * @author Forge
 * @date 2026-04-11
 * @version 2.0.0
 */
function DrawLesson({ lesson }) {
  const unitName = lesson.lessonOrTest.lesson
    ? lesson.lessonOrTest.lesson.unit.name
    : "Unknown";
  const lessonName = lesson.lessonOrTest.lesson
    ? lesson.lessonOrTest.lesson.name
    : "Unknown";
  const unitId = lesson.lessonOrTest.lesson
    ? lesson.lessonOrTest.lesson.unit._id
    : "";
  const lessonId = lesson.lessonOrTest.lesson
    ? lesson.lessonOrTest.lesson._id
    : "";

  return (
    <div className="bg-white border border-slate-200 rounded-[10px] p-5 shadow-[0_8px_30px_rgba(0,0,0,0.08)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.12)] hover:border-slate-300 transition-all duration-150">
      {/* Header: Time, Room, Level */}
      <div className="flex items-center justify-between gap-4 mb-3">
        <div className="flex items-center gap-3">
          {lesson.startTime && (
            <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600 bg-slate-50 px-2 py-1 rounded-[8px]">
              <Clock size={13} className="text-slate-400" />
              {lesson.startTime}
              {lesson.endTime ? ` – ${lesson.endTime}` : ""}
            </div>
          )}
          {lesson.room && (
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <MapPin size={13} className="text-slate-400" />
              {lesson.room}
            </div>
          )}
        </div>
        {lesson.level && (
          <span className="text-[0.65rem] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full uppercase tracking-wider">
            {lesson.level}
          </span>
        )}
      </div>

      {/* Body */}
      <div className="flex gap-6 items-start">
        {/* Class & Syllabus Info */}
        <div className="min-w-[280px] max-w-[340px] flex-1">
          <Link
            to={`/class/${lesson.htClass}`}
            className="text-sm font-bold text-slate-900 hover:text-teal-600 transition-colors duration-150"
          >
            {lesson.name}
          </Link>

          <div className="mt-1.5 flex items-center gap-1.5 text-xs">
            <BookOpen size={12} className="text-slate-400" />
            {lesson.syllabus && lesson.syllabus.name ? (
              <Link
                to={`/teaching/syllabus/${lesson.syllabus._id}`}
                className="text-teal-600 hover:text-teal-800 hover:underline font-medium"
              >
                {lesson.syllabus.name}
              </Link>
            ) : (
              <span className="text-slate-400 italic">No syllabus</span>
            )}
          </div>

          <div className="text-xs text-slate-500 mt-1">
            Lesson {lesson.courseLesson} of {lesson.totalLessons}
          </div>

          <div className="mt-1.5 text-xs">
            <Link
              to={`/teaching/syllabus/${lesson.syllabus._id}/unit/${unitId}/lesson/${lessonId}`}
              className="text-slate-500 hover:text-teal-600 transition-colors duration-150"
            >
              <span className="font-medium text-slate-700">
                U{lesson.unitNumber}:
              </span>{" "}
              {unitName}
              {" · "}
              <span className="font-medium text-slate-700">
                L{lesson.lessonNumber}:
              </span>{" "}
              {lessonName}
            </Link>
          </div>
        </div>

        {/* Teachers */}
        <Teachers lesson={lesson} />

        {/* Materials */}
        {lesson.materialRequests && (
          <div className="w-36 min-w-0 self-start">
            <div className="flex items-center gap-1.5 mb-1">
              <FileText size={12} className="text-slate-400" />
              <div className="text-[0.65rem] font-semibold text-slate-400 uppercase tracking-wider">
                Materials
              </div>
            </div>
            <div className="text-xs text-slate-600 leading-relaxed">
              {lesson.materialRequests}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Teachers - displays VN and foreign teacher info with lesson plan links.
 * @component
 * @param {Object} props
 * @param {Object} props.lesson
 * @author Forge
 * @date 2026-04-11
 * @version 2.0.0
 */
function Teachers({ lesson }) {
  const vnLessonPlan = lesson.lessonOrTest.lesson?.otherLessonPlans.find(
    (lp) => lp.addedBy === lesson.teacher
  );
  const ftLessonPlan = lesson.lessonOrTest.lesson?.otherLessonPlans.find(
    (lp) => lp.addedBy === lesson.foreignTeacher
  );

  return (
    <div className="flex flex-col gap-3 min-w-[180px]">
      <div>
        <div className="flex items-center gap-1.5 mb-1">
          <Users size={12} className="text-slate-400" />
          <div className="text-[0.65rem] font-semibold text-slate-400 uppercase tracking-wider">
            VN Teacher
          </div>
        </div>
        <div className="text-xs text-slate-700">
          <Staff id={lesson.teacher} />
        </div>
        <div className="w-36 truncate text-xs mt-0.5">
          {vnLessonPlan ? (
            <a
              href={
                "https://docs.google.com/document/d/" + vnLessonPlan.fileId + "/"
              }
              target="_blank"
              rel="noopener noreferrer"
              className="text-teal-600 hover:text-teal-800 hover:underline"
            >
              {vnLessonPlan.name}
            </a>
          ) : (
            <span className="text-rose-500 bg-rose-50 px-1.5 py-0.5 rounded text-[0.65rem]">
              No lesson plan
            </span>
          )}
        </div>
      </div>

      {lesson.foreignTeacher && (
        <div>
          <div className="flex items-center gap-1.5 mb-1">
            <Users size={12} className="text-slate-400" />
            <div className="text-[0.65rem] font-semibold text-slate-400 uppercase tracking-wider">
              FT Teacher
            </div>
          </div>
          <div className="text-xs text-slate-700">
            <Staff id={lesson.foreignTeacher} />
          </div>
          <div className="w-36 truncate text-xs mt-0.5">
            {ftLessonPlan ? (
              <a
                href={
                  "https://docs.google.com/document/d/" +
                  ftLessonPlan.fileId +
                  "/"
                }
                target="_blank"
                rel="noopener noreferrer"
                className="text-teal-600 hover:text-teal-800 hover:underline"
              >
                {ftLessonPlan.name}
              </a>
            ) : (
              <span className="text-rose-500 bg-rose-50 px-1.5 py-0.5 rounded text-[0.65rem]">
                No lesson plan
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
